import { OutlineElement } from '../../base/outline-element/outline-element';
import { html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-countdown-card.css.lit';
import { CountdownTracker } from './tracker';
import { Clock } from './clock';
import { calculateTimeRemaining } from './time';

/**
 * The Countdown Clock component.
 */
@customElement('ghs-countdown-clock')
export class CountdownFlipClock extends OutlineElement {
  static styles = [componentStyles];

  @property({ type: String }) countdownEnd: string;
  @state() private _countdownTrackers: Record<string, CountdownTracker[]> = {};

  private _debounceTimeout: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._setupCountdownClock();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('countdownEnd')) {
      const oldValue = changedProperties.get('countdownEnd');
      if (oldValue !== this.countdownEnd) {
        this._debounce(this._setupCountdownClock.bind(this), 1);
      }
    }
  }

  private _debounce(func: () => void, wait: number) {
    if (this._debounceTimeout) {
      clearTimeout(this._debounceTimeout);
    }
    this._debounceTimeout = window.setTimeout(() => {
      this._debounceTimeout = null;
      func();
    }, wait);
  }

  private _setupCountdownClock() {
    const deadline = this._getDeadline();
    this._initializeCountdownTrackers(deadline);
  }

  private _initializeCountdownTrackers(deadline: Date) {
    const clock = new Clock(deadline, () => {});
    this._countdownTrackers = clock.trackers;
  }

  private _getDeadline(): Date {
    return new Date(this.countdownEnd);
  }

  render(): TemplateResult {
    const completeTime = this._formatRemainingTime();
    return html`
      <div class="countdown-clock" role="timer" aria-label="${completeTime}">
        ${this._renderCountdownSegments()}
      </div>
    `;
  }

  private _renderCountdownSegments(): TemplateResult[] {
    return Object.keys(this._countdownTrackers).map(
      key => html`
        <div class="container" aria-hidden="true">
          <div class="heading">${key}</div>
          <div class="digit-group">
            ${this._countdownTrackers[key].map(tracker => html`${tracker.el}`)}
          </div>
        </div>
      `
    );
  }

  private _formatRemainingTime(): string {
    const timeData = calculateTimeRemaining(this._getDeadline());
    return `${timeData.Days} days, ${timeData.Hours} hours, ${timeData.Minutes} minutes, ${timeData.Seconds} seconds`;
  }
}
